import { RequestField } from '../../../types/responses/Jira';

export type FieldKind =
  | 'summary' | 'text' | 'textarea' | 'datetime' | 'date' | 'number' | 'select' | 'attachment' | 'user';

const HIDDEN_IDS = new Set(['reporter', 'raiseonbehalfof']);
const HIDDEN_NAMES = ['abrir em nome', 'raise this request on behalf'];
// Campo "Organizations" do JSM: o backend injeta a organizacao e descarta o valor do cliente.
const HIDDEN_CUSTOM = 'sd-customer-organizations';

export const fieldKind = (f: RequestField): FieldKind => {
  const type = f.jiraSchema?.type;
  if (f.fieldId === 'summary' || f.jiraSchema?.system === 'summary') return 'summary';
  if (type === 'array' && f.jiraSchema?.items === 'attachment') return 'attachment';
  if (type === 'user') return 'user';
  if (type === 'datetime') return 'datetime';
  if (type === 'date') return 'date';
  if (type === 'number') return 'number';
  if (type === 'option' && f.validValues?.length) return 'select';
  if ((type === 'string' || !type) && /descri/i.test(f.name)) return 'textarea';
  return 'text';
};

const isHidden = (f: RequestField): boolean =>
  HIDDEN_IDS.has(f.fieldId.toLowerCase()) ||
  HIDDEN_NAMES.some((n) => f.name.toLowerCase().includes(n)) ||
  (f.jiraSchema?.custom || '').toLowerCase().includes(HIDDEN_CUSTOM);

// Resumo primeiro, depois obrigatórios antes de opcionais; dentro de cada grupo, ordem original do
// backend. Comparador explícito por índice (não depende de sort ser estável) — achado de review:
// se o backend intercalar campo obrigatório e opcional na mesma tela, o form os mantém juntos.
export const visibleFields = (fields: RequestField[]): RequestField[] => {
  const shown = fields.filter((f) => !isHidden(f)).map((f, index) => ({ f, index }));
  shown.sort((a, b) => {
    const byKind = Number(fieldKind(a.f) !== 'summary') - Number(fieldKind(b.f) !== 'summary');
    if (byKind !== 0) return byKind;
    const byRequired = Number(!a.f.required) - Number(!b.f.required);
    if (byRequired !== 0) return byRequired;
    return a.index - b.index;
  });
  return shown.map(({ f }) => f);
};

const pad = (n: number, len = 2) => String(n).padStart(len, '0');

/**
 * `<input type="datetime-local">` entrega `2026-08-20T09:00` (hora local, sem offset). O Jira espera
 * `yyyy-MM-dd'T'HH:mm:ss.SSSZ` com offset sem dois pontos (ex.: `-0300`). Só o navegador conhece o
 * fuso do usuario, entao a conversao e feita aqui. Entrada invalida volta inalterada.
 */
export const toJiraDateTime = (localValue: string): string => {
  const d = new Date(localValue);
  if (!localValue || Number.isNaN(d.getTime())) return localValue;
  const offsetMin = -d.getTimezoneOffset();
  const abs = Math.abs(offsetMin);
  return (
    `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}` +
    `${offsetMin < 0 ? '-' : '+'}${pad(Math.floor(abs / 60))}${pad(abs % 60)}`
  );
};

export const validate = (
  fields: RequestField[],
  values: Record<string, any>,
  doneAttachments = 0,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  fields.forEach((f) => {
    const kind = fieldKind(f);
    const required = f.required || kind === 'summary';
    if (!required || kind === 'user') return;
    if (kind === 'attachment') {
      if (doneAttachments < 1) errors[f.fieldId] = 'Required field';
      return;
    }
    const v = values[f.fieldId];
    if (v === undefined || v === null || String(v).trim() === '') errors[f.fieldId] = 'Required field';
  });
  return errors;
};

export const buildPayload = (
  requestTypeId: string,
  values: Record<string, any>,
  temporaryAttachmentIds: string[],
  onBehalfOfTenantPk?: number,
  kinds: Record<string, FieldKind> = {},
) => {
  const fields: Record<string, string> = {};
  Object.entries(values).forEach(([k, v]) => {
    if (v !== undefined && v !== null && String(v).trim() !== '') fields[k] = kinds[k] === 'datetime' ? toJiraDateTime(String(v)) : String(v);
  });
  const payload: {
    requestTypeId: string;
    fields: Record<string, string>;
    temporaryAttachmentIds: string[];
    onBehalfOfTenantPk?: number;
  } = { requestTypeId, fields, temporaryAttachmentIds };
  if (onBehalfOfTenantPk !== undefined) payload.onBehalfOfTenantPk = onBehalfOfTenantPk;
  return payload;
};
